import React, { useEffect, useRef, useState } from 'react';
import ItemSlot from '../../ui/ItemSlot';
import GameAPI from './GameAPI';
import { GameItemTooltip } from './Game_ItemTooltip';
import { ItemArtwork, ItemQuantity } from './ItemSlotContent';
import './QuickBelt.css';
import { isTouchPointer } from './touchItemDrag';

export default function QuickBelt({
  capacity = 1,
  disabled = false,
  draggedItem = null,
  entries = [],
  management = false,
  onDragEnd,
  onDragStart,
  onTransfer,
  onUse,
}) {
  const [loadedAt, setLoadedAt] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  const suppressClickRef = useRef(false);
  const tooltipPinnedRef = useRef(false);
  const safeCapacity = Math.min(8, Math.max(1, Number(capacity) || 1));
  const slots = Array.from({ length: safeCapacity }, (_, slotIndex) => (
    entries.find(entry => Number(entry.slotIndex) === slotIndex) || null
  ));
  const canAccept = management
    && !disabled
    && !draggedItem?.quantity
    && ['inventory', 'quickBelt'].includes(draggedItem?.fromComponent);

  useEffect(() => {
    let active = true;
    const refs = entries.map(entry => ({
      itemId: entry.itemId,
      itemInstanceId: entry.itemInstanceId || '',
    }));
    if (refs.length === 0) return undefined;
    GameAPI.fetchItemsAuto(refs).finally(() => {
      if (active) setLoadedAt(Date.now());
    });
    return () => {
      active = false;
    };
  }, [entries]);

  const transferTo = slotIndex => {
    if (!canAccept) return;
    const source = draggedItem;
    if (source.fromComponent === 'inventory') {
      onTransfer?.({
        direction: 'packToBelt',
        itemId: source.item.itemId,
        sourceX: Number(source.rowIndex),
        sourceY: Number(source.colIndex),
        destinationSlotIndex: slotIndex,
      });
    } else if (Number(source.slotIndex) !== slotIndex) {
      onTransfer?.({
        direction: 'beltToBelt',
        itemId: source.item.itemId,
        sourceSlotIndex: Number(source.slotIndex),
        destinationSlotIndex: slotIndex,
      });
    } else {
      onDragEnd?.();
    }
  };

  const pickUp = (entry, slotIndex) => {
    if (!entry || disabled) return;
    onDragStart?.({
      item: entry,
      rowIndex: -1,
      colIndex: -1,
      fromComponent: 'quickBelt',
      slotIndex,
    });
  };

  const closeTooltip = () => {
    tooltipPinnedRef.current = false;
    setTooltip(null);
  };

  const showTooltip = (entry, anchor, interactive) => {
    if (!interactive && tooltipPinnedRef.current) return;
    tooltipPinnedRef.current = interactive;
    setTooltip({ entry, anchor, interactive });
  };

  useEffect(() => {
    if (!tooltip) return;
    const source = tooltip.entry;
    const sourceStillCurrent = entries.some(entry => (
      Number(entry.slotIndex) === Number(source.slotIndex)
      && (source.itemInstanceId
        ? entry.itemInstanceId === source.itemInstanceId
        : entry.itemId === source.itemId)
      && Number(entry.quantity) === Number(source.quantity)
    ));
    if (!sourceStillCurrent) {
      tooltipPinnedRef.current = false;
      setTooltip(null);
    }
  }, [entries, tooltip]);

  const endPointer = (event, entry, slotIndex) => {
    if (!isTouchPointer(event)) return;
    if (suppressClickRef.current || disabled) return;
    suppressClickRef.current = true;
    if (management) {
      if (canAccept) transferTo(slotIndex);
      else if (entry) showTooltip(entry, event.currentTarget, true);
    } else if (entry) {
      showTooltip(entry, event.currentTarget, true);
    }
  };

  const itemActions = tooltip ? [
    ...(onUse ? [{
      id: 'use',
      label: 'Use',
      disabled,
      disabledReason: disabled ? 'Unavailable during your current action' : '',
      onSelect: () => {
        closeTooltip();
        onUse(tooltip.entry, GameAPI.fetchItemAutoCached(
          tooltip.entry.itemId,
          tooltip.entry.itemInstanceId,
        ));
      },
    }] : []),
    ...(management ? [{
      id: 'move',
      label: 'Move',
      disabled,
      disabledReason: disabled ? 'Unavailable during your current action' : '',
      onSelect: () => {
        const slotIndex = Number(tooltip.anchor?.dataset.slotIndex);
        closeTooltip();
        pickUp(tooltip.entry, slotIndex);
      },
    }] : []),
  ] : [];

  return (
    <div className={`quick-belt ${management ? 'quick-belt--manage' : 'quick-belt--hud'}`}>
      <span className="quick-belt__label">Belt</span>
      <div
        aria-label={management ? 'Quick belt management' : 'Quick belt'}
        className="quick-belt__slots"
        data-items-loaded-at={loadedAt || undefined}
        role="list"
      >
        {slots.map((entry, slotIndex) => {
          const item = entry && GameAPI.fetchItemAutoCached(
            entry.itemId,
            entry.itemInstanceId,
          );
          const rarity = item && GameAPI.fetchRarityCached(item.rarityId);
          const selected = draggedItem?.fromComponent === 'quickBelt'
            && Number(draggedItem.slotIndex) === slotIndex;
          return (
            <ItemSlot
              aria-label={`Belt slot ${slotIndex + 1}${item ? `, ${item.name}, quantity ${entry.quantity}` : ', empty'}`}
              className={`quick-belt__slot${canAccept ? ' quick-belt__slot--compatible' : ''}`}
              item={item}
              itemType={GameAPI.fetchItemTypeCached(item?.typeId)}
              key={`belt-${slotIndex}`}
              onClick={() => {
                if (suppressClickRef.current) {
                  suppressClickRef.current = false;
                  return;
                }
                if (disabled) return;
                if (management) transferTo(slotIndex);
                else if (entry) onUse?.(entry, item);
              }}
              onDragOver={event => {
                if (canAccept) event.preventDefault();
              }}
              onDrop={event => {
                event.preventDefault();
                transferTo(slotIndex);
              }}
              onKeyDown={event => {
                if (canAccept && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  transferTo(slotIndex);
                }
              }}
              rarity={rarity?.name?.toLowerCase()}
              rarityColor={rarity?.colorHex}
              role="listitem"
              selected={selected}
              tabIndex={canAccept ? 0 : -1}
            >
              {entry ? (
                <button
                  aria-label={`${management ? 'Manage' : 'Use'} ${item?.name || 'belt item'}, quantity ${entry.quantity}`}
                  aria-disabled={disabled || undefined}
                  className="quick-belt__item"
                  data-slot-index={slotIndex}
                  draggable={management && !disabled}
                  title={management ? undefined : `Use ${item?.name || 'belt item'}`}
                  onBlur={() => {
                    if (!tooltipPinnedRef.current) closeTooltip();
                  }}
                  onContextMenu={event => {
                    event.preventDefault();
                    showTooltip(entry, event.currentTarget, true);
                  }}
                  onDragEnd={onDragEnd}
                  onDragStart={event => {
                    event.stopPropagation();
                    closeTooltip();
                    event.dataTransfer?.setData('text/plain', entry.itemId);
                    pickUp(entry, slotIndex);
                  }}
                  onFocus={event => showTooltip(entry, event.currentTarget, false)}
                  onKeyDown={event => {
                    if (disabled) return;
                    if (event.key === ' ') {
                      event.preventDefault();
                      management ? pickUp(entry, slotIndex) : onUse?.(entry, item);
                    } else if (event.key === 'Enter') {
                      event.preventDefault();
                      management ? transferTo(slotIndex) : onUse?.(entry, item);
                    }
                  }}
                  onMouseEnter={event => showTooltip(entry, event.currentTarget, false)}
                  onMouseLeave={() => {
                    if (!tooltipPinnedRef.current) closeTooltip();
                  }}
                  onPointerMove={event => {
                    if (!isTouchPointer(event)) {
                      showTooltip(entry, event.currentTarget, false);
                    }
                  }}
                  onPointerUp={event => endPointer(event, entry, slotIndex)}
                  type="button"
                >
                  <ItemArtwork
                    item={item}
                    itemId={entry.itemId}
                  />
                  <ItemQuantity quantity={entry.quantity} />
                </button>
              ) : null}
            </ItemSlot>
          );
        })}
      </div>
      {tooltip ? (
        <GameItemTooltip
          anchorRect={tooltip.anchor?.getBoundingClientRect()}
          getAnchorElement={() => tooltip.anchor}
          getAnchorRect={() => tooltip.anchor?.getBoundingClientRect()}
          actions={itemActions}
          interactive={tooltip.interactive}
          itemId={tooltip.entry.itemId}
          itemInstanceId={tooltip.entry.itemInstanceId || ''}
          onRequestClose={closeTooltip}
        />
      ) : null}
    </div>
  );
}
